/**
 * Custom SVG dice icons for the dice roller.
 * Each icon represents the characteristic shape of its die type.
 */

interface DiceIconProps {
  className?: string;
}

/** D4 - Tetrahedron (pyramid) */
export function D4Icon({ className = '' }: DiceIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Triangle base with 3D effect */}
      <path d="M12 3L3 20h18L12 3z" />
      {/* Center line for 3D depth */}
      <path d="M12 3v12" strokeOpacity="0.5" />
      <path d="M12 15L3 20" strokeOpacity="0.5" />
      <path d="M12 15l9 5" strokeOpacity="0.5" />
    </svg>
  );
}

/** D6 - Isometric cube showing 3 faces */
export function D6Icon({ className = '' }: DiceIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Isometric cube - 3 visible faces */}
      {/* Top face */}
      <path d="M12 2L22 7L12 12L2 7L12 2z" />
      {/* Left face */}
      <path d="M2 7v10l10 5V12L2 7z" />
      {/* Right face */}
      <path d="M22 7v10l-10 5V12l10-5z" />
      {/* Pip on top face */}
      <circle cx="12" cy="7" r="1.2" fill="currentColor" stroke="none" />
      {/* Pips on left face (2 pips diagonal) */}
      <circle cx="5" cy="10" r="0.9" fill="currentColor" stroke="none" fillOpacity="0.7" />
      <circle cx="9" cy="16" r="0.9" fill="currentColor" stroke="none" fillOpacity="0.7" />
      {/* Pips on right face (3 pips diagonal) */}
      <circle cx="19" cy="10" r="0.9" fill="currentColor" stroke="none" fillOpacity="0.7" />
      <circle cx="17" cy="13" r="0.9" fill="currentColor" stroke="none" fillOpacity="0.7" />
      <circle cx="15" cy="16" r="0.9" fill="currentColor" stroke="none" fillOpacity="0.7" />
    </svg>
  );
}

/** D8 - Octahedron (two pyramids) */
export function D8Icon({ className = '' }: DiceIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Diamond shape */}
      <path d="M12 2L22 12L12 22L2 12L12 2z" />
      {/* Horizontal center line for 3D */}
      <path d="M2 12h20" strokeOpacity="0.5" />
      {/* Vertical facet lines */}
      <path d="M12 2v20" strokeOpacity="0.3" />
    </svg>
  );
}

/** D10 - Pentagonal trapezohedron (kite shape) */
export function D10Icon({ className = '' }: DiceIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Kite/diamond with pointed top */}
      <path d="M12 1L20 8L16 22H8L4 8L12 1z" />
      {/* Center vertical line */}
      <path d="M12 1v21" strokeOpacity="0.4" />
      {/* Cross facet */}
      <path d="M4 8h16" strokeOpacity="0.4" />
    </svg>
  );
}

/** D12 - Dodecahedron (pentagon-based) */
export function D12Icon({ className = '' }: DiceIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Pentagon outline */}
      <path d="M12 2L22 9L18 21H6L2 9L12 2z" />
      {/* Inner pentagon for 3D depth */}
      <path d="M12 6L17 10L15 17H9L7 10L12 6z" strokeOpacity="0.5" />
      {/* Connecting lines */}
      <path d="M12 2v4M22 9l-5 1M18 21l-3-4M6 21l3-4M2 9l5 1" strokeOpacity="0.3" />
    </svg>
  );
}

/** D20 - Icosahedron (triangular faces) */
export function D20Icon({ className = '' }: DiceIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Hexagonal outline */}
      <path d="M12 2L21 7V17L12 22L3 17V7L12 2z" />
      {/* Inner triangular facets */}
      <path d="M12 2v20" strokeOpacity="0.4" />
      <path d="M3 7l18 10" strokeOpacity="0.4" />
      <path d="M21 7L3 17" strokeOpacity="0.4" />
      {/* Center point */}
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" fillOpacity="0.5" />
    </svg>
  );
}

/** D100 - Percentile (stylized percent or two D10s) */
export function D100Icon({ className = '' }: DiceIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Two overlapping D10 shapes */}
      {/* Back die */}
      <path d="M14 3L20 7L17 16H11L8 7L14 3z" strokeOpacity="0.4" />
      {/* Front die */}
      <path d="M10 8L16 12L13 21H7L4 12L10 8z" />
      {/* Front die center line */}
      <path d="M10 8v13" strokeOpacity="0.3" />
    </svg>
  );
}

/** Modifier icon (plus/minus) */
export function ModifierIcon({ className = '' }: DiceIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Plus sign */}
      <path d="M12 5v6M9 8h6" />
      {/* Minus sign */}
      <path d="M9 16h6" />
      {/* Divider */}
      <path d="M6 12h12" strokeOpacity="0.3" />
    </svg>
  );
}

